/**
 * Каркас портала: вход, меню по правам, аватар и выход (§5.1 п. 1–2).
 *
 * Вход и отказ — из каркаса платформы (`LoginScreen`, `Denied`, разбор
 * `401`/`403`) и ведут себя так же, как в остальных сборках. Своё здесь
 * только раскладка: у портала меню разделов слева, а на телефоне — панель
 * снизу, чего у `AppShell` нет и в каркас на одну сборку не заносится.
 */

import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '@shell/api';
import type { LoginConfig } from '@shell/api';
import { useSession } from '@shell/AppShell';
import { setRestaurantTimeZone } from '@shell/format';
import { isMiniApp } from '@shell/miniapp';
import { Denied, ErrorState, Loading } from '@shell/states';
import { LoginScreen } from '@shell/TelegramLogin';
import type { TelegramAuthResult } from '@shell/TelegramLogin';
import { useTheme } from '@shell/theme';
import { portalApi } from './api';
import type { Navigation } from './api';
import { Icon } from './icons';
import { CopyGuard } from '@shell/CopyGuard';
import { GROUPS, sectionForPath, visibleSections } from './nav';
import type { SectionDef } from './nav';
import { Link, useRoute } from './router';

export function PortalShell({
  children,
}: {
  children: (nav: Navigation, sections: SectionDef[]) => ReactNode;
}) {
  const queryClient = useQueryClient();
  const miniApp = isMiniApp();
  const [loginPending, setLoginPending] = useState(false);
  const [loginProblem, setLoginProblem] = useState<string | undefined>();

  const config = useQuery<LoginConfig, ApiError>({
    queryKey: ['login-config'],
    queryFn: () => api.loginConfig(),
    staleTime: Infinity,
    enabled: !miniApp,
  });
  if (config.data?.restaurant_tz) setRestaurantTimeZone(config.data.restaurant_tz);

  const session = useSession();
  const nav = useQuery<Navigation, ApiError>({
    queryKey: ['navigation'],
    queryFn: () => portalApi.navigation(),
    enabled: Boolean(session.data),
    refetchOnWindowFocus: true,
  });

  const onAuth = useCallback(
    (user: TelegramAuthResult) => {
      setLoginPending(true);
      setLoginProblem(undefined);
      api
        .loginWithTelegram(user as unknown as Record<string, unknown>)
        .then((me) => {
          queryClient.setQueryData(['me'], me);
          return queryClient.invalidateQueries();
        })
        .catch((error: unknown) => {
          if (error instanceof ApiError && error.isForbidden) {
            setLoginProblem(
              'Войти не получилось. Если вы новый сотрудник, запросите доступ через бота.',
            );
          } else if (error instanceof ApiError && error.status === 503) {
            setLoginProblem('Вход на этом сервере ещё не настроен — сообщите администратору.');
          } else {
            setLoginProblem('Telegram подтвердил вход, но сервер его не принял. Попробуйте ещё раз.');
          }
        })
        .finally(() => setLoginPending(false));
    },
    [queryClient],
  );

  const onLogout = useCallback(() => {
    api
      .logout()
      .catch(() => undefined)
      .finally(() => queryClient.clear());
  }, [queryClient]);

  if ((config.isPending && !miniApp) || session.isPending) {
    return <Loading />;
  }

  if (session.error?.isUnauthorized && miniApp) {
    return (
      <ErrorState
        title="Сессия Telegram устарела"
        text="Закройте окно и откройте портал заново кнопкой из бота."
        onRetry={() => session.refetch()}
      />
    );
  }

  if (session.error?.isUnauthorized) {
    return (
      <div className="portal-login">
        <LoginScreen
          botUsername={config.data?.bot_username ?? ''}
          loginAvailable={config.data?.login_available ?? false}
          onAuth={onAuth}
          pending={loginPending}
          failedText={loginProblem}
        />
      </div>
    );
  }

  if (session.error?.isForbidden || nav.error?.isForbidden) {
    return <Denied botUsername={config.data?.bot_username} />;
  }

  if (session.error || nav.error) {
    return (
      <ErrorState
        error={session.error ?? nav.error}
        onRetry={() => (session.error ? session.refetch() : nav.refetch())}
      />
    );
  }

  if (!nav.data) return <Loading />;

  const sections = visibleSections(nav.data);
  const name = nav.data.user.name ?? session.data?.full_name ?? 'Без имени';

  return (
    <div className="portal">
      <Sidebar sections={sections} name={name} onLogout={miniApp ? undefined : onLogout} />
      <main className="portal-main">{children(nav.data, sections)}</main>
      <BottomBar sections={sections} name={name} onLogout={miniApp ? undefined : onLogout} />
      <CopyGuard allowed={Boolean(nav.data.user.can_copy)} name={name} />
    </div>
  );
}

function initials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  return (parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '') || 'Т';
}

function NavItem({ section, active }: { section: SectionDef; active: boolean }) {
  return (
    <Link
      to={section.path}
      className={`nav-item${active ? ' nav-item--active' : ''}`}
      aria-current={active ? 'page' : undefined}
      title={section.title}
    >
      <Icon name={section.code} />
      <span className="nav-item__label">{section.title}</span>
      {!section.ready && <span className="nav-item__soon">скоро</span>}
    </Link>
  );
}

function ThemeButton({ withLabel }: { withLabel?: boolean }) {
  const [theme, toggle] = useTheme();
  const label = theme === 'dark' ? 'Светлая тема' : 'Тёмная тема';
  return (
    <button type="button" className="side-action" onClick={toggle} aria-label={label} title={label}>
      <Icon name={theme === 'dark' ? 'sun' : 'moon'} />
      {withLabel && <span className="side-action__label">{label}</span>}
    </button>
  );
}

const COLLAPSED_KEY = 'portal.sidebar.collapsed';

/** Свёрнуто ли меню: выбор человека запоминается в браузере; без выбора —
 *  свёрнуто на узком экране. Хранилище может быть недоступно — тогда просто
 *  не запоминаем. */
function useCollapsed(): [boolean, (value: boolean) => void] {
  const [collapsed, setState] = useState<boolean>(() => {
    try {
      const saved = window.localStorage.getItem(COLLAPSED_KEY);
      if (saved !== null) return saved === '1';
    } catch {
      /* без хранилища */
    }
    return typeof window !== 'undefined' && window.innerWidth < 1100;
  });
  const set = (value: boolean) => {
    setState(value);
    try {
      window.localStorage.setItem(COLLAPSED_KEY, value ? '1' : '0');
    } catch {
      /* без хранилища */
    }
  };
  return [collapsed, set];
}

function Sidebar({
  sections,
  name,
  onLogout,
}: {
  sections: SectionDef[];
  name: string;
  onLogout?: () => void;
}) {
  const { path } = useRoute();
  const current = sectionForPath(path);
  const [collapsed, setCollapsed] = useCollapsed();
  const label = collapsed ? 'Развернуть меню' : 'Свернуть меню';
  return (
    <aside className={`sidebar${collapsed ? ' sidebar--collapsed' : ''}`} aria-label="Разделы">
      <div className="sidebar__head">
        <Link to="/" className="sidebar__brand" aria-label="Тоскана — на главную">
          <span className="logo">Т</span>
          <span className="sidebar__brand-name">Тоскана</span>
        </Link>
        <button
          type="button"
          className="side-action sidebar__toggle"
          onClick={() => setCollapsed(!collapsed)}
          aria-label={label}
          aria-expanded={!collapsed}
          title={label}
        >
          <Icon name="panel" />
        </button>
      </div>
      <nav className="sidebar__nav">
        {GROUPS.map((group) => {
          const inGroup = sections.filter((section) => section.group === group);
          if (inGroup.length === 0) return null;
          return (
            <div className="nav-group" key={group}>
              <div className="nav-group__title">{group}</div>
              {inGroup.map((section) => (
                <NavItem
                  key={section.code}
                  section={section}
                  active={current?.code === section.code}
                />
              ))}
            </div>
          );
        })}
      </nav>
      <div className="sidebar__foot">
        <div className="me" title={name}>
          <span className="avatar">{initials(name)}</span>
          <span className="me__name">{name}</span>
        </div>
        <div className="sidebar__actions">
          <ThemeButton />
          {onLogout && (
            <button
              type="button"
              className="side-action"
              onClick={onLogout}
              aria-label="Выйти"
              title="Выйти"
            >
              <Icon name="logout" />
            </button>
          )}
        </div>
      </div>
    </aside>
  );
}

function BottomBar({
  sections,
  name,
  onLogout,
}: {
  sections: SectionDef[];
  name: string;
  onLogout?: () => void;
}) {
  const { path } = useRoute();
  const current = sectionForPath(path);
  const [open, setOpen] = useState(false);
  const pinned = sections.filter((section) => section.code === 'today' || section.code === 'receipts');
  const rest = sections.filter((section) => !pinned.includes(section));
  const restActive = rest.some((section) => section.code === current?.code);

  return (
    <>
      {open && (
        <div className="sheet-backdrop" role="presentation" onClick={() => setOpen(false)}>
          <div
            className="sheet"
            role="dialog"
            aria-label="Все разделы"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="sheet__head">
              <span className="avatar">{initials(name)}</span>
              <span className="me__name">{name}</span>
              <span className="spacer" />
              <button
                type="button"
                className="side-action"
                onClick={() => setOpen(false)}
                aria-label="Закрыть"
              >
                <Icon name="close" />
              </button>
            </div>
            {rest.map((section) => (
              <Link
                key={section.code}
                to={section.path}
                className={`nav-item${current?.code === section.code ? ' nav-item--active' : ''}`}
                onClick={() => setOpen(false)}
              >
                <Icon name={section.code} />
                <span className="nav-item__label">{section.title}</span>
                {!section.ready && <span className="nav-item__soon">скоро</span>}
              </Link>
            ))}
            <div className="sheet__actions">
              <ThemeButton withLabel />
              {onLogout && (
                <button type="button" className="side-action" onClick={onLogout}>
                  <Icon name="logout" />
                  <span className="side-action__label">Выйти</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      <nav className="tabbar" aria-label="Разделы">
        {pinned.map((section) => (
          <Link
            key={section.code}
            to={section.path}
            className={`tab${current?.code === section.code ? ' tab--active' : ''}`}
          >
            <Icon name={section.code} size={20} />
            <span>{section.title}</span>
          </Link>
        ))}
        <button
          type="button"
          className={`tab${restActive ? ' tab--active' : ''}`}
          onClick={() => setOpen(true)}
        >
          <Icon name="more" size={20} />
          <span>Ещё</span>
        </button>
      </nav>
    </>
  );
}
